import type { Program } from "./schemas/program.js";
import type { Exercise } from "./schemas/exercise.js";
import type { PlannedSet } from "./schemas/plan.js";
import { EFFORT_HARD_SET_RIR } from "./effortScale.js";
import { resolveFullCycleSessions, type ResolvedSession } from "./programResolution.js";
import { catalogBySlug, resolveExerciseMuscleCredits } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// A prescribed set counts as a HARD set — the SAME thing hardSetsByMuscle counts
// in the logged actuals — unless the program explicitly targets it submaximal.
// The actual counts a logged set when its effective RIR ≤ EFFORT_HARD_SET_RIR
// (≈ RPE ≥ 7); mirror that on the prescription's target effort. Most program
// sets carry no explicit RPE/RIR target (percent-of-max / absolute working
// sets) — those ARE the working sets, so "no target" = assumed hard, and only a
// deliberately easy target (RIR ≥ 4 / RPE ≤ 6 — the meter's bottom `4+`
// step) drops out. Without this filter
// the readout would count ramp/back-off/technique volume the actual never
// credits, systematically overstating the program's ask.
// Review fix (PE-22 K3 triad, Warning consolidation): the SAME three-way rule
// once lived twice — here against a resolved PlannedSet's targetRir/targetRpe,
// and again in hardSetsByMuscleFromDraft.ts against the Draft's per-row
// intensityKind + per-week cell value. Both sites now call this pure decision;
// each maps its own shape down to "the two numbers a resolved set would
// carry" and nothing else changes.
export const isPrescribedHardSetByEffort = (
  targetRir: number | null | undefined,
  targetRpe: number | null | undefined,
): boolean => {
  if (targetRir != null) return targetRir <= EFFORT_HARD_SET_RIR;
  if (targetRpe != null) return targetRpe >= 10 - EFFORT_HARD_SET_RIR;
  return true; // no explicit effort target → a working set, assumed hard
};

export const isPrescribedHardSet = (s: PlannedSet): boolean =>
  isPrescribedHardSetByEffort(s.targetRir, s.targetRpe);

// AP-7 (2026-07-07). What the PROGRAM prescribes per muscle each week — the
// planning-side counterpart to hardSetsByMuscle's logged actuals. Grounds the
// target editor ("the program prescribes ~11/wk" next to "you average 10.4")
// and lets the meter tell "no coverage" (the program trains this muscle 0×)
// apart from "building 0/12" (prescribed, not yet trained this week).
//
// Walks the FULL macrocycle via resolveFullCycleSessions, so alternating /
// phase-offset variants resolve to the exercises actually trained each week,
// counts each resolved entry's HARD planned sets (isPrescribedHardSet — the
// same effort boundary the actual uses), and attributes them with the SAME
// piecewise credit the actuals use (resolveExerciseMuscleCredits, AP-6) — so
// "prescribes N" and the meter's actual are the same kind of number and compare
// like-for-like. Averaged over cycleWeeks for the per-week figure.
//
// Programs don't model warm-ups (no schema flag — warm-ups are done in-session,
// never prescribed); the effort filter, not a warm-up flag, keeps easy volume
// out. Catalog misses (custom or untagged rows) contribute nothing, same as the
// volume math.
// HARD prescribed sets for ONE resolved day, per exercise slug — the per-session
// primitive both the weekly average (below) and the per-day PROJECTION (AP-10,
// programTargetProjection) build on, so "what the program prescribes" is counted
// in exactly one place. A hard set is weight-independent (it's a set count), so
// this counts directly, never through the volume-gated session scorer.
export const prescribedHardSetsByExercise = (
  session: ResolvedSession,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const entry of session.entries) {
    const hardSets = entry.sets.filter(isPrescribedHardSet).length;
    if (hardSets > 0) {
      out[entry.exerciseId] = (out[entry.exerciseId] ?? 0) + hardSets;
    }
  }
  return out;
};

export const programWeeklyPrescription = (
  program: Program,
  catalog: ReadonlyArray<Exercise>,
): Record<string, number> => {
  const cycleWeeks = program.cycleWeeks;
  if (!Number.isInteger(cycleWeeks) || cycleWeeks < 1) return {};
  const bySlug = catalogBySlug(catalog);
  const out: Record<string, number> = {};
  for (const session of resolveFullCycleSessions(program)) {
    for (const [slug, hardSets] of Object.entries(
      prescribedHardSetsByExercise(session),
    )) {
      const ex = bySlug.get(slug);
      if (!ex) continue;
      for (const [m, credit] of resolveExerciseMuscleCredits(
        ex,
        HARD_SET_MUSCLE_CONTRIB_MIN,
      )) {
        out[m] = (out[m] ?? 0) + hardSets * credit;
      }
    }
  }
  for (const m of Object.keys(out)) {
    out[m] = (out[m] ?? 0) / cycleWeeks;
  }
  return out;
};
